import React, { useState, useEffect } from 'react';
import { permissionsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Shield,
  Save,
  RotateCcw,
  Loader2,
  Lock,
  Check,
  X,
  AlertTriangle
} from 'lucide-react';

const ROLE_LABELS = {
  admin: { label: 'Amministratore', color: 'bg-red-600', description: 'Accesso completo al sistema' },
  amministrazione: { label: 'Amministrazione', color: 'bg-purple-600', description: 'Finance e HR' },
  coordinator: { label: 'Coordinator', color: 'bg-blue-600', description: 'Gestione operativa' },
  pm: { label: 'Project Manager', color: 'bg-green-600', description: 'Gestione commesse assegnate' },
  risorsa: { label: 'Risorsa', color: 'bg-gray-600', description: 'Rendicontazione ore' },
};

export const PermissionManagementPage = () => {
  const { user } = useAuth();
  const [structure, setStructure] = useState({});
  const [rolesPermissions, setRolesPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeRole, setActiveRole] = useState('amministrazione');
  const [editedPermissions, setEditedPermissions] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const isAdmin = user?.ruolo === 'admin';

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Check for changes
    const currentRole = rolesPermissions.find(r => r.role === activeRole);
    if (currentRole && editedPermissions[activeRole]) {
      const original = JSON.stringify(currentRole.permissions);
      const edited = JSON.stringify(editedPermissions[activeRole]);
      setHasChanges(original !== edited);
    } else {
      setHasChanges(false);
    }
  }, [editedPermissions, activeRole, rolesPermissions]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [structureRes, rolesRes] = await Promise.all([
        permissionsAPI.getStructure(),
        permissionsAPI.getAllRoles()
      ]);
      
      setStructure(structureRes.data);
      setRolesPermissions(rolesRes.data);
      
      // Initialize edited permissions
      const edited = {};
      rolesRes.data.forEach(r => {
        edited[r.role] = { ...r.permissions };
      });
      setEditedPermissions(edited);
    } catch (error) {
      console.error('Error loading permissions:', error);
      toast.error('Errore nel caricamento dei permessi');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePermissionChange = (module, action, value) => {
    if (activeRole === 'admin') return; // Cannot edit admin
    
    setEditedPermissions(prev => ({
      ...prev,
      [activeRole]: {
        ...prev[activeRole],
        [module]: {
          ...prev[activeRole]?.[module],
          [action]: value
        }
      }
    }));
  };

  const handleSave = async () => {
    if (activeRole === 'admin') return;
    
    setIsSaving(true);
    try {
      await permissionsAPI.updateRolePermissions(activeRole, editedPermissions[activeRole]);
      toast.success(`Permessi per ${ROLE_LABELS[activeRole]?.label || activeRole} salvati`);
      
      // Update local state
      setRolesPermissions(prev => 
        prev.map(r => r.role === activeRole ? { ...r, permissions: editedPermissions[activeRole], is_default: false } : r)
      );
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving permissions:', error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Errore nel salvataggio');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (activeRole === 'admin') return;
    
    setIsSaving(true);
    try {
      const res = await permissionsAPI.resetRolePermissions(activeRole);
      toast.success(`Permessi per ${ROLE_LABELS[activeRole]?.label || activeRole} ripristinati`);
      
      // Update local state with defaults
      setRolesPermissions(prev => 
        prev.map(r => r.role === activeRole ? { ...r, permissions: res.data.permissions, is_default: true } : r)
      );
      setEditedPermissions(prev => ({
        ...prev,
        [activeRole]: res.data.permissions
      }));
      setHasChanges(false);
      setResetDialogOpen(false);
    } catch (error) {
      console.error('Error resetting permissions:', error);
      toast.error('Errore nel ripristino');
    } finally {
      setIsSaving(false);
    }
  };

  const discardChanges = () => {
    const currentRole = rolesPermissions.find(r => r.role === activeRole);
    if (currentRole) {
      setEditedPermissions(prev => ({
        ...prev,
        [activeRole]: { ...currentRole.permissions }
      }));
    }
    setHasChanges(false);
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Solo gli Amministratori possono gestire i permessi</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="permissions-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-12 w-full max-w-2xl" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  const currentRoleData = rolesPermissions.find(r => r.role === activeRole);
  const currentPermissions = editedPermissions[activeRole] || {};

  return (
    <div className="space-y-6" data-testid="permissions-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Gestione Autorizzazioni
          </h1>
          <p className="text-muted-foreground">Configura i permessi per ogni ruolo del sistema</p>
        </div>
        {hasChanges && activeRole !== 'admin' && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={discardChanges}>
              Annulla modifiche
            </Button>
            <Button onClick={handleSave} disabled={isSaving} data-testid="save-permissions-btn">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Salva Permessi
            </Button>
          </div>
        )}
      </div>

      {/* Role Tabs */}
      <Tabs value={activeRole} onValueChange={setActiveRole}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          {Object.entries(ROLE_LABELS).map(([role, info]) => (
            <TabsTrigger 
              key={role} 
              value={role}
              className="flex items-center gap-2"
              data-testid={`role-tab-${role}`}
            >
              <span className={`w-2 h-2 rounded-full ${info.color}`} />
              {info.label}
              {role === 'admin' && <Lock className="h-3 w-3 text-muted-foreground" />}
            </TabsTrigger>
          ))}
        </TabsList>

        {Object.keys(ROLE_LABELS).map(role => (
          <TabsContent key={role} value={role} className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Badge className={ROLE_LABELS[role]?.color}>{ROLE_LABELS[role]?.label}</Badge>
                      {currentRoleData?.is_default && role !== 'admin' && (
                        <Badge variant="outline" className="text-xs">Default</Badge>
                      )}
                      {!currentRoleData?.is_default && role !== 'admin' && (
                        <Badge variant="secondary" className="text-xs">Personalizzato</Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {ROLE_LABELS[role]?.description}
                    </CardDescription>
                  </div>
                  {role !== 'admin' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setResetDialogOpen(true)}
                      data-testid="reset-permissions-btn"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Ripristina Default
                    </Button>
                  )}
                </div>
                {role === 'admin' && (
                  <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg flex items-center gap-2 text-amber-800 dark:text-amber-200">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="text-sm">I permessi Admin non possono essere modificati per motivi di sicurezza</span>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Object.entries(structure).map(([moduleKey, moduleData]) => (
                    <Card key={moduleKey} className="bg-muted/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">{moduleData.label}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {Object.entries(moduleData.actions).map(([actionKey, actionLabel]) => {
                          const isEnabled = currentPermissions[moduleKey]?.[actionKey] ?? false;
                          const isDisabled = role === 'admin';
                          
                          return (
                            <div 
                              key={actionKey} 
                              className="flex items-center justify-between"
                            >
                              <Label 
                                htmlFor={`${role}-${moduleKey}-${actionKey}`}
                                className={`text-sm ${isDisabled ? 'text-muted-foreground' : ''}`}
                              >
                                {actionLabel}
                              </Label>
                              <div className="flex items-center gap-2">
                                {isEnabled ? (
                                  <Check className="h-4 w-4 text-emerald-600" />
                                ) : (
                                  <X className="h-4 w-4 text-muted-foreground" />
                                )}
                                <Switch
                                  id={`${role}-${moduleKey}-${actionKey}`}
                                  checked={isEnabled}
                                  onCheckedChange={(value) => handlePermissionChange(moduleKey, actionKey, value)}
                                  disabled={isDisabled}
                                  data-testid={`switch-${role}-${moduleKey}-${actionKey}`}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent data-testid="reset-dialog">
          <DialogHeader>
            <DialogTitle>Ripristina Permessi Default</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler ripristinare i permessi di default per il ruolo <strong>{ROLE_LABELS[activeRole]?.label}</strong>?
              Questa azione sovrascriverà tutte le modifiche personalizzate.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleReset} disabled={isSaving} data-testid="confirm-reset-btn">
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Ripristina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PermissionManagementPage;
